// Frontend mirror of the locked backend issue transition guard. Drives the
// composer's "Status update" dropdown (only valid targets are offered) so the
// UI never lets a user attempt a transition the server would reject. The map
// must stay byte-for-byte in sync with the backend's canTransitionIssue — the
// exhaustive table test in canTransitionIssue.test.ts documents the full
// matrix so a reviewer can eyeball FE↔BE parity.
//
// Lifecycle: open → in_progress / awaiting_response / resolved / closed;
// in_progress and awaiting_response can move between each other (via resolved
// for in_progress, directly back for awaiting_response's "response received"),
// to resolved, or to closed; resolved can re-open or close. Closed is terminal
// in this guard — any re-open is a deliberate, separately-confirmed action
// outside the standard composer dropdown, so the guard exposes no closed→*
// edge. Identity (from === to) is never a transition.

import type { IssueStatus } from "../../../../types";

/** The full allowed-target map. Each key's array is the exact set of statuses
 *  reachable from that state. Closed maps to an empty set (terminal). */
export const ISSUE_TRANSITIONS: Record<IssueStatus, IssueStatus[]> = {
  open: ["in_progress", "awaiting_response", "resolved", "closed"],
  in_progress: ["awaiting_response", "resolved", "closed"],
  awaiting_response: ["in_progress", "resolved", "closed"],
  resolved: ["open", "closed"],
  closed: [],
};

/** True when `to` is a permitted transition target from `from`. Identity
 *  pairs and any target absent from the map return false. */
export function canTransitionIssue(from: IssueStatus, to: IssueStatus): boolean {
  return ISSUE_TRANSITIONS[from].includes(to);
}

/** The permitted transition targets from a given status — the set the
 *  composer's status dropdown offers. */
export function allowedIssueTransitions(from: IssueStatus): IssueStatus[] {
  return ISSUE_TRANSITIONS[from];
}
